import { useState, useEffect } from "react";
import {
  agnesAddPantry, agnesRemovePantry, agnesAddShopping, agnesToggleShopping,
  agnesRemoveShopping, agnesClearCheckedShopping, agnesAddGarden, agnesRemoveGarden,
  agnesGetGardenReminders,
} from "@/lib/api";
import { X, Plus, Trash2, Sprout, Droplets, Scissors, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ACTION_ICON = { water: Droplets, harvest: Scissors, feed: Sparkles, sow: Sprout, tend: Sprout };

export const PantryDrawer = ({ open, setOpen, tab, setTab, pantry, setPantry, shopping, setShopping, garden, setGarden }) => {
  const [pName, setPName] = useState("");
  const [pQty, setPQty] = useState("");
  const [sName, setSName] = useState("");
  const [gName, setGName] = useState("");
  const [gWhen, setGWhen] = useState("");
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (open && tab === "garden" && garden && garden.length > 0) {
      agnesGetGardenReminders().then((r) => setReminders(r.reminders || [])).catch(() => {});
    }
  }, [open, tab, garden]);

  const addPantry = async (e) => {
    e.preventDefault();
    if (!pName.trim()) return;
    try { setPantry(await agnesAddPantry({ name: pName.trim(), quantity: pQty.trim() })); setPName(""); setPQty(""); }
    catch { toast.error("Could not add that to your pantry."); }
  };
  const addShop = async (e) => {
    e.preventDefault();
    if (!sName.trim()) return;
    try { setShopping(await agnesAddShopping({ name: sName.trim() })); setSName(""); }
    catch { toast.error("Could not add that to your list."); }
  };
  const toggle = async (it) => { try { setShopping(await agnesToggleShopping(it.id, !it.checked)); } catch {} };
  const checkedCount = shopping.filter((s) => s.checked).length;

  const addGarden = async (e) => {
    e.preventDefault();
    if (!gName.trim()) return;
    try { setGarden(await agnesAddGarden({ name: gName.trim(), planted_on: gWhen.trim() })); setGName(""); setGWhen(""); }
    catch { toast.error("Could not add that to your garden journal."); }
  };

  return (
    <>
      {open && <div className="fixed inset-0 bg-brand-charcoal/40 z-[60]" onClick={() => setOpen(false)} />}
      <aside className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-brand-cream z-[70] shadow-2xl transition-transform duration-500 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="pantry-panel">
        <div className="flex items-center justify-between px-6 h-16 border-b border-brand-linen shrink-0">
          <span className="font-serif text-2xl">Your Larder</span>
          <button onClick={() => setOpen(false)} data-testid="pantry-close" className="text-brand-charcoal"><X size={22} /></button>
        </div>
        <div className="flex border-b border-brand-linen shrink-0">
          {[["pantry", "Pantry"], ["shopping", `Shopping${shopping.length ? ` · ${shopping.length}` : ""}`], ["garden", "Garden"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`larder-tab-${k}`}
              className={`flex-1 py-3 font-sans text-[0.66rem] uppercase tracking-[0.1em] transition-colors ${tab === k ? "bg-brand-warm-white text-brand-charcoal border-b-2 border-brand-gold" : "text-brand-muted"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "pantry" ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <p className="font-body text-sm text-brand-muted mb-4">Agnes cooks around what you keep here. Tell her "I've run out of eggs" in chat and she'll tidy it for you.</p>
              {pantry.length === 0 ? (
                <p className="font-body text-brand-muted italic py-8 text-center">Your larder is empty just now.</p>
              ) : (
                <ul className="space-y-2" data-testid="pantry-list">
                  {pantry.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 bg-brand-warm-white border border-brand-linen px-4 py-3" data-testid={`pantry-item-${it.id}`}>
                      <div className="flex-1">
                        <span className="font-body text-brand-charcoal">{it.name}</span>
                        {it.quantity && <span className="font-sans text-xs text-brand-muted ml-2">· {it.quantity}</span>}
                      </div>
                      <button onClick={async () => setPantry(await agnesRemovePantry(it.id))} data-testid={`pantry-remove-${it.id}`} className="text-brand-muted hover:text-brand-copper"><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <form onSubmit={addPantry} className="border-t border-brand-linen px-6 py-5 shrink-0 space-y-3">
              <input value={pName} onChange={(e) => setPName(e.target.value)} data-testid="pantry-add-name" placeholder="Add an item (e.g. plain flour)"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
              <div className="flex gap-3">
                <input value={pQty} onChange={(e) => setPQty(e.target.value)} data-testid="pantry-add-qty" placeholder="Quantity (optional)"
                  className="flex-1 bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
                <button type="submit" data-testid="pantry-add-btn" className="bg-brand-olive text-brand-cream px-5 flex items-center gap-2 font-sans text-xs uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors">
                  <Plus size={15} /> Add
                </button>
              </div>
            </form>
          </>
        ) : tab === "shopping" ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <p className="font-body text-sm text-brand-muted mb-4">Anything you tell Agnes you're low on lands here. Tick things off as you shop.</p>
              {shopping.length === 0 ? (
                <p className="font-body text-brand-muted italic py-8 text-center">Your shopping list is empty.</p>
              ) : (
                <ul className="space-y-2" data-testid="shopping-list">
                  {shopping.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 bg-brand-warm-white border border-brand-linen px-4 py-3" data-testid={`shopping-item-${it.id}`}>
                      <button onClick={() => toggle(it)} data-testid={`shopping-toggle-${it.id}`}
                        className={`w-5 h-5 border shrink-0 flex items-center justify-center ${it.checked ? "bg-brand-sage border-brand-sage" : "border-brand-gold"}`}>
                        {it.checked && <span className="text-brand-cream text-xs">✓</span>}
                      </button>
                      <div className="flex-1">
                        <span className={`font-body ${it.checked ? "line-through text-brand-muted" : "text-brand-charcoal"}`}>{it.name}</span>
                        {it.quantity && <span className="font-sans text-xs text-brand-muted ml-2">· {it.quantity}</span>}
                      </div>
                      <button onClick={async () => setShopping(await agnesRemoveShopping(it.id))} className="text-brand-muted hover:text-brand-copper"><Trash2 size={15} /></button>
                    </li>
                  ))}
                </ul>
              )}
              {checkedCount > 0 && (
                <button onClick={async () => setShopping(await agnesClearCheckedShopping())} data-testid="shopping-clear-checked"
                  className="mt-4 w-full font-sans text-[0.7rem] uppercase tracking-[0.14em] text-brand-copper py-2 hover:text-brand-charcoal">
                  Clear {checkedCount} purchased
                </button>
              )}
            </div>
            <form onSubmit={addShop} className="border-t border-brand-linen px-6 py-5 shrink-0 flex gap-3">
              <input value={sName} onChange={(e) => setSName(e.target.value)} data-testid="shopping-add-name" placeholder="Add to shopping list"
                className="flex-1 bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
              <button type="submit" data-testid="shopping-add-btn" className="bg-brand-olive text-brand-cream px-5 flex items-center gap-2 font-sans text-xs uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors">
                <Plus size={15} /> Add
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <p className="font-body text-sm text-brand-muted mb-4">Keep a note of what you've planted, and Agnes will tend it with you through the seasons.</p>
              {reminders.length > 0 && (
                <div className="mb-5 bg-brand-cream border border-brand-linen p-4" data-testid="garden-reminders">
                  <span className="overline text-[0.56rem]">Agnes's nudges</span>
                  <ul className="mt-3 space-y-3">
                    {reminders.map((r, i) => {
                      const Ic = ACTION_ICON[r.action] || Sprout;
                      return (
                        <li key={i} className="flex gap-3" data-testid={`garden-reminder-${i}`}>
                          <Ic size={15} className="text-brand-sage shrink-0 mt-0.5" />
                          <p className="font-body text-sm text-brand-body leading-snug"><span className="text-brand-charcoal">{r.plant}:</span> {r.note}</p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {(!garden || garden.length === 0) ? (
                <p className="font-body text-brand-muted italic py-8 text-center">Your garden journal is empty.</p>
              ) : (
                <ul className="space-y-2" data-testid="garden-list">
                  {garden.map((it) => (
                    <li key={it.id} className="flex items-start gap-3 bg-brand-warm-white border border-brand-linen px-4 py-3" data-testid={`garden-item-${it.id}`}>
                      <Sprout size={16} className="text-brand-sage shrink-0 mt-1" />
                      <div className="flex-1">
                        <span className="font-body text-brand-charcoal">{it.name}</span>
                        {it.planted_on && <span className="font-sans text-xs text-brand-muted ml-2">· {it.planted_on}</span>}
                        {it.notes && <p className="font-body text-sm text-brand-muted">{it.notes}</p>}
                      </div>
                      <button onClick={async () => setGarden(await agnesRemoveGarden(it.id))} data-testid={`garden-remove-${it.id}`} className="text-brand-muted hover:text-brand-copper"><Trash2 size={15} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <form onSubmit={addGarden} className="border-t border-brand-linen px-6 py-5 shrink-0 space-y-3">
              <input value={gName} onChange={(e) => setGName(e.target.value)} data-testid="garden-add-name" placeholder="What did you plant? (e.g. Sweet peas)"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
              <div className="flex gap-3">
                <input value={gWhen} onChange={(e) => setGWhen(e.target.value)} data-testid="garden-add-when" placeholder="When? (optional)"
                  className="flex-1 bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
                <button type="submit" data-testid="garden-add-btn" className="bg-brand-olive text-brand-cream px-5 flex items-center gap-2 font-sans text-xs uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors">
                  <Plus size={15} /> Add
                </button>
              </div>
            </form>
          </>
        )}
      </aside>
    </>
  );
};
